import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy-load all non-critical routes to shrink the initial bundle
const History = lazy(() => import("./pages/History"));
const Rules = lazy(() => import("./pages/Rules"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Core = lazy(() => import("./pages/Core"));
const Partners = lazy(() => import("./pages/Partners"));
const WallOfLove = lazy(() => import("./pages/WallOfLove"));
const PastSessions = lazy(() => import("./pages/PastSessions"));
const StartDivision = lazy(() => import("./pages/StartDivision"));
const Terms = lazy(() => import("./pages/Terms"));
const PartnerWithUs = lazy(() => import("./pages/PartnerWithUs"));
const About = lazy(() => import("./pages/About"));
const Divisions = lazy(() => import("./pages/Divisions"));
const DivisionDetail = lazy(() => import("./pages/DivisionDetail"));
const DivisionHeadLogin = lazy(() => import("./pages/DivisionHeadLogin"));
const DivisionHeadManage = lazy(() => import("./pages/DivisionHeadManage"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Careers = lazy(() => import("./pages/Careers"));
const Legal = lazy(() => import("./pages/Legal"));
const Founder = lazy(() => import("./pages/Founder"));
const Sponsorship = lazy(() => import("./pages/Sponsorship"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/history" element={<History />} />
              <Route path="/rules" element={<Rules />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="/core" element={<Core />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/wall-of-love" element={<WallOfLove />} />
              <Route path="/past-sessions" element={<PastSessions />} />
              <Route path="/start-division" element={<StartDivision />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/partner-with-us" element={<PartnerWithUs />} />
              <Route path="/about" element={<About />} />
              <Route path="/divisions" element={<Divisions />} />
              <Route path="/division/:slug" element={<DivisionDetail />} />
              <Route path="/division/:slug/login" element={<DivisionHeadLogin />} />
              <Route path="/division/:slug/manage" element={<DivisionHeadManage />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/founder" element={<Founder />} />
              <Route path="/sponsorship" element={<Sponsorship />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
